import type { CSSProperties, FunctionComponent, ReactNode } from 'react';

import {
    StyledScrollArea,
    StyledScrollAreaCorner,
    StyledScrollAreaScrollbar,
    StyledScrollAreaThumb,
    StyledScrollAreaViewport,
} from '../../core/scroll-area/ScrollArea';
import { TileBase } from '../../core/tile/Tile';

export type Content = {
    readonly id: number | string;
    readonly content: ReactNode;
    readonly flex?: number;
    readonly minWidth?: string;
    readonly visible?: boolean;
    readonly width: string;
};

export type BoardProps = {
    readonly content: readonly Content[];
};

export const Sidebar: FunctionComponent<{ readonly children: ReactNode; readonly className?: string }> = ({
    children,
    className,
}) => <div className={`bg-white-200 rounded-lg h-full p-m ${className ?? ''}`}>{children}</div>;

// https://github.com/radix-ui/primitives/issues/2964
// fix for display: table
const CroppedScrollArea: FunctionComponent<{
    readonly children: ReactNode;
    readonly style?: CSSProperties;
}> = ({ children, style }) => (
    <StyledScrollArea style={style}>
        <StyledScrollAreaViewport
            className="scroll-area-viewport"
            style={{ height: '100%', scrollSnapType: 'x mandatory' }}
        >
            <div
                className="flex flex-row gap-xxl min-w-full w-fit [&>*:last-child]:pr-m"
                style={{ height: '100%', paddingLeft: 8 }}
            >
                {children}
            </div>
        </StyledScrollAreaViewport>
        <StyledScrollAreaScrollbar
            orientation="horizontal"
            style={{ bottom: '1px', left: 8, right: 8, width: 'calc(100% - 16px)' }}
        >
            <StyledScrollAreaThumb />
        </StyledScrollAreaScrollbar>
        <StyledScrollAreaCorner />
    </StyledScrollArea>
);

export const SPRING_CONFIG = {
    damping: 40,
    mass: 0.8,
    stiffness: 400,
    type: 'spring',
} as const;

export const BoardContentWithHeader: FunctionComponent<{
    readonly content: ReactNode;
    readonly header: ReactNode;
    readonly id?: string;
}> = ({ content, header, id }) => {
    return (
        <TileBase
            className="border border-gray-300 min-h-0 p-m [&_.scroll-area-viewport>div]:!block [&_.scroll-area-viewport>div]:h-full"
            id={id}
        >
            <div className="flex items-center bg-white-200 rounded-xl pl-m pr-m pt-m w-full">{header}</div>
            <CroppedScrollArea style={{ flex: 1, minHeight: 0 }}>{content}</CroppedScrollArea>
        </TileBase>
    );
};
